/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { AutocompleteItemDto, AutocompleteItemMapper, ID } from '@gh/common-shared-data';
import {
  AbstractRestService, arrayOf, createPrivilegedTokenEntity, emptyOf, identity, none, optimisticLockingOf, PrivilegedToken, privilegedTokenOf, RestSettings, SlicedDataSource, } from '@gh/core-data';
import { map$, switchAll$ } from '@gh/rx/operators';
import { omit } from 'lodash';
import { Observable } from 'rxjs';
import { CheckUsageDto, CheckUsageMapper } from '../common';
import {
  PbCalculatedCcDto,
  PbCalculatedRequestDto,
  PbCcElementDto,
  PbDetailsDto,
  PbDetailsSaveDto,
  PbDetailsSaveResponseDto,
  PbGetCostCenterRequestDto,
  PbHousePlanCalculateResponseDto,
  PbHousePlanDto,
  PbHousePlanSaveDto,
  PbHousePlansRequestDto,
  PbLinkCsmGetResponseDto,
  PbLinkJstGetResponseDto,
  PbLinkSaveRequestDto,
  PbLinkSaveResponseDto,
  PriceBookFilterDto,
  PriceBookListItemDto,
} from './price-book.dto';
import {
  PbCalculatedCcMapper,
  PbCcStructureMapper,
  PbDetailsMapper,
  PbDetailsSaveMapper,
  PbDetailsSaveResponseMapper,
  PbGetCostCenterRequestMapper,
  PbHousePlanCalculateResponseMapper,
  PbHousePlanMapper,
  PbHousePlanSaveMapper,
  PbHousePlansRequestMapper,
  PbLinkCsmResponseMapper,
  PbLinkJstResponseMapper,
  PbLinkSaveRequestMapper,
  PriceBookFilterMapper,
  PriceBookListItemMapper,
} from './price-book.mapper';
import { PbDetailsGetItemsComponentsRequest } from '../../builder-estimating/price-books/resources/price-book.model';

@Injectable()
export class PriceBookRestService extends AbstractRestService {
  constructor(settings: RestSettings) {
    super(settings);
  }

  findAllAsAutocompleteDataSource(query: Partial<PriceBookFilterDto>): SlicedDataSource<AutocompleteItemDto> {
    return this.createSlicedDataSourceGet(
      `${this.bp}/price-books`,
      { ...query, autocomplete: true, showActive: true },
      PriceBookFilterMapper,
      arrayOf(AutocompleteItemMapper));
  }

  findAllAsDataSource(filter: PriceBookFilterDto): SlicedDataSource<PriceBookListItemDto> {
    return this.createSlicedDataSourceGet(
      `${this.bp}/price-books`, filter, PriceBookFilterMapper, arrayOf(PriceBookListItemMapper));
  }

  checkUsage(priceBook: PriceBookListItemDto): Observable<CheckUsageDto> {
    return this.httpGet(
      `${this.bp}/price-books/check-usage/${priceBook.priceBookId}`,
      { version: priceBook.version },
      void 0,
      CheckUsageMapper,
    );
  }

  get(pbId: ID): Observable<PbDetailsDto> {
    return this.httpGet(`${this.bp}/price-books/${pbId}`, optimisticLockingOf(PbDetailsMapper));
  }

  clone(pbId: ID): Observable<PbDetailsDto> {
    return this.httpGet(`${this.bp}/price-books/${pbId}/clone`, optimisticLockingOf(PbDetailsMapper));
  }

  create(pbDetails: PbDetailsSaveDto): Observable<PbDetailsSaveResponseDto> {
    return this.httpPost(
      `${this.bp}/price-books/new/calculate`,
      omit(pbDetails, 'priceBookId'),
      PbDetailsSaveMapper,
      optimisticLockingOf(PbDetailsSaveResponseMapper));
  }

  update(pbDetails: PbDetailsSaveDto): Observable<PbDetailsSaveResponseDto> {
    return this.httpPost(
      `${this.bp}/price-books/${pbDetails.priceBookId}/calculate`,
      omit(pbDetails, 'priceBookId'),
      optimisticLockingOf(PbDetailsSaveMapper),
      optimisticLockingOf(PbDetailsSaveResponseMapper));
  }

  calculate(pbDetails: PbDetailsSaveDto): Observable<PbDetailsSaveResponseDto> {
    return this.httpPost(
      `${this.bp}/price-books/${pbDetails.priceBookId || 'new'}/calculate`,
      omit(pbDetails, 'priceBookId'),
      optimisticLockingOf(PbDetailsSaveMapper),
      PbDetailsSaveResponseMapper);
  }

  reprice(priceBookId: ID): Observable<PbDetailsDto> {
    return this.httpPost(`${this.bp}/price-books/${priceBookId}/reprice`, void 0, none(), none()).pipe(
      map$(() => this.get(priceBookId)),
      switchAll$());
  }

  applyDefaults(priceBookId: ID, privilegedToken?: PrivilegedToken): Observable<PbDetailsDto> {
    return this.httpPost(
      `${this.bp}/price-books/${priceBookId}/apply-defaults`,
      privilegedToken && createPrivilegedTokenEntity('PriceBookApplyDefaults', privilegedToken),
      emptyOf(privilegedTokenOf(identity())),
      none()).pipe(
      map$(() => this.get(priceBookId)),
      switchAll$());
  }

  calculateHousePlan(hp: PbHousePlanSaveDto): Observable<PbHousePlanCalculateResponseDto> {
    return this.httpPost(
      `${this.bp}/price-books/house-plans/calculate`,
      hp,
      PbHousePlanSaveMapper,
      PbHousePlanCalculateResponseMapper);
  }

  validateHousePlanRemoval(priceBookId: ID, housePlanId: ID, code: string): Observable<void> {
    return this.httpGet(
      `${this.bp}/price-books/${priceBookId}/house-plans/${housePlanId}/validate-removal`,
      { code },
      void 0,
      none());
  }

  remove(priceBook: PriceBookListItemDto, checkUsage: CheckUsageDto): Observable<CheckUsageDto> {
    return this.httpDelete(
      `${this.bp}/price-books/${priceBook.priceBookId}`, checkUsage, CheckUsageMapper);
  }

  findRelatedHousePlansAsDataSource(request: PbHousePlansRequestDto): SlicedDataSource<PbHousePlanDto> {
    return this.createSlicedDataSourcePost(
      `${this.bp}/price-books/${request.priceBookId || 'new'}/house-plans`,
      <PbHousePlansRequestDto>omit(request, 'priceBookId'),
      optimisticLockingOf(PbHousePlansRequestMapper),
      arrayOf(PbHousePlanMapper));
  }

  findRelatedHousePlansAsFinderDataSource(request: PbHousePlansRequestDto): SlicedDataSource<PbHousePlanDto> {
    return this.createSlicedDataSourcePost(
      `${this.bp}/price-books/${request.priceBookId}/house-plans/finder`,
      <PbHousePlansRequestDto>omit(request, 'priceBookId'),
      optimisticLockingOf(PbHousePlansRequestMapper),
      arrayOf(PbHousePlanMapper));
  }

  findRelatedCalculatedHierarchy(priceBookId: ID, request: PbCalculatedRequestDto): Observable<PbCalculatedCcDto[]> {
    return this.httpGet(
      `${this.bp}/price-books/${priceBookId}/iac`,
      request,
      identity(),
      arrayOf(PbCalculatedCcMapper));
  }

  findRelatedCcStructures(request: PbGetCostCenterRequestDto): Observable<PbCcElementDto[]> {
    return this.httpPost(
      `${this.bp}/price-books/${request.priceBookId}/cost-centers/${request.costCenterId}`,
      omit(request, 'priceBookId', 'costCenterId'),
      PbGetCostCenterRequestMapper,
      arrayOf(PbCcStructureMapper));
  }

  getAllItemsByPB(request: PbDetailsGetItemsComponentsRequest): Observable<PbCcElementDto[]> {
    return this.httpPost(
      `${this.bp}/price-books/${request.priceBookId}/all-cost-centers`,
      omit(request, 'priceBookId'),
      identity(),
      identity(),
    );
  }

  getJstLinks(priceBookId: ID): Observable<PbLinkJstGetResponseDto> {
    return this.httpGet(`${this.bp}/price-books/${priceBookId}/link-with-spec-templates`, PbLinkJstResponseMapper);
  }

  saveJstLinks(priceBookId: ID, request: PbLinkSaveRequestDto): Observable<PbLinkSaveResponseDto> {
    return this.httpPut(
      `${this.bp}/price-books/${priceBookId}/link-with-spec-templates`,
      request,
      PbLinkSaveRequestMapper,
      identity());
  }

  getCsmLinks(priceBookId: ID): Observable<PbLinkCsmGetResponseDto> {
    return this.httpGet(`${this.bp}/price-books/${priceBookId}/link-with-cs-masters`, PbLinkCsmResponseMapper);
  }

  saveCsmLinks(priceBookId: ID, request: PbLinkSaveRequestDto): Observable<PbLinkSaveResponseDto> {
    return this.httpPut(
      `${this.bp}/price-books/${priceBookId}/link-with-cs-masters`,
      request,
      PbLinkSaveRequestMapper,
      identity());
  }

  getHousePlanList(params: any): Observable<any> {
    return this.httpPost(`${this.bp}/price-books/${params.priceBookId}/all-house-plans`,
                         omit(params, 'priceBookId'),
                         optimisticLockingOf(PbHousePlansRequestMapper),
                         arrayOf(PbHousePlanMapper));
  }
}
